import { useState, useEffect, useRef, useMemo } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { DIETS } from '../lib/constants'

function useGridCols() {
  const [cols, setCols] = useState(() => window.innerWidth >= 640 ? 2 : 1)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)')
    const update = e => setCols(e.matches ? 2 : 1)
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return cols
}

export default function Discover({ currentUserId, users, loading, loadingMore, hasMoreUsers, currentUser, requests, dietFilter, setDietFilter, cityFilter, setCityFilter, availableCities, onlyAvailable, setOnlyAvailable, mealTime, setMealTime, loadUsers, loadMoreUsers, toggleAvailability, sendInvite, inviteStatus, onLogin, onOpenProfile }) {

  function getRequestStatus(userId) {
    if (!currentUserId) return null
    return requests.find(r =>
      (r.senderId === currentUserId && r.receiverId === userId) ||
      (r.senderId === userId && r.receiverId === currentUserId)
    ) || null
  }
  const [filtersOpen, setFiltersOpen] = useState(false)
  const cols = useGridCols()
  const listRef = useRef(null)

  // Keep latest loadMoreUsers in a ref so the scroll handler never goes stale
  const loadMoreRef = useRef(loadMoreUsers)
  useEffect(() => { loadMoreRef.current = loadMoreUsers }, [loadMoreUsers])

  // Trigger loadMoreUsers when user scrolls within 400px of the bottom
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 400) {
        loadMoreRef.current?.()
      }
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  const activeCount = [dietFilter, cityFilter, onlyAvailable].filter(Boolean).length

  const rows = useMemo(() => {
    const r = []
    for (let i = 0; i < users.length; i += cols) r.push(users.slice(i, i + cols))
    return r
  }, [users, cols])

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 340,
    overscan: 3,
  })

  return (
    <section className="flex flex-col h-full">
      {currentUser && <div className="fade-up glass-card rounded-3xl border border-white/60 p-4 sm:p-5 shadow-xl shrink-0">

        {/* Mobile toggle row */}
        <div className="flex items-center justify-between sm:hidden">
          <button
            onClick={() => setFiltersOpen(v => !v)}
            className="flex items-center gap-2 rounded-2xl border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 active:bg-orange-50"
          >
            <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M7 12h10M11 20h2" />
            </svg>
            Filters
            {activeCount > 0 && (
              <span className="rounded-full bg-orange-500 text-white text-xs w-5 h-5 flex items-center justify-center">{activeCount}</span>
            )}
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            {currentUser && (
              <button onClick={toggleAvailability} className="rounded-full border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100">
                {currentUser.availability?.isAvailable ? '🟢 Available' : '⭕ Unavailable'}
              </button>
            )}
            <button onClick={loadUsers} className="rounded-full bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white">↻</button>
          </div>
        </div>

        {/* Filter grid — always visible on sm+, collapsible on mobile */}
        <div className={`${filtersOpen ? 'mt-3' : 'hidden'} sm:block sm:mt-0`}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <select className="rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm" value={dietFilter} onChange={e => setDietFilter(e.target.value)}>
              <option value="">All Diets</option>
              {DIETS.map(d => <option key={d}>{d}</option>)}
            </select>
            <div className="relative">
              <input
                list="city-options"
                placeholder="Search city…"
                value={cityFilter}
                onChange={e => setCityFilter(e.target.value)}
                className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm"
              />
              {cityFilter && (
                <button onClick={() => setCityFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs">✕</button>
              )}
              <datalist id="city-options">
                {availableCities.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <input type="datetime-local" className="rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm" value={mealTime} onChange={e => setMealTime(e.target.value)} />
            <label className="flex items-center justify-center gap-2 rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 cursor-pointer">
              <input type="checkbox" checked={onlyAvailable} onChange={e => setOnlyAvailable(e.target.checked)} />
              Available Only
            </label>
            <button onClick={() => { loadUsers(); setFiltersOpen(false) }} className="rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black">Apply</button>
          </div>
          {currentUser && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button onClick={toggleAvailability} className="rounded-full border border-gray-800 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-100">
                {currentUser.availability?.isAvailable ? '🟢 Mark Unavailable' : '⭕ Mark Available'}
              </button>
              {inviteStatus && <p className="text-sm font-medium text-green-700">{inviteStatus}</p>}
            </div>
          )}
        </div>

        {inviteStatus && <p className="mt-2 text-sm font-medium text-green-700 sm:hidden">{inviteStatus}</p>}
      </div>}

      <div ref={listRef} className="mt-6 flex-1 min-h-0 overflow-y-auto">
        {!currentUser && (
          <div className="fade-up px-2 py-6 mb-4">
            <h1 className="title-font text-3xl font-bold text-gray-900 sm:text-5xl">Find Dining Partners Nearby</h1>
            <p className="mt-2 text-sm text-gray-600 sm:text-base">Discover people who love food as much as you do. Meet, eat, repeat.</p>
            <button onClick={onLogin} className="mt-5 rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-6 py-3 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600">
              🍽️ Get Started — Login or Sign Up
            </button>
          </div>
        )}
        {loading && <p className="text-sm font-semibold text-gray-600 p-1">Loading users...</p>}
        {!loading && users.length === 0 && <p className="text-sm font-semibold text-gray-600 p-1">No matching diners found.</p>}
        {!loading && rows.length > 0 && (
          <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map(vRow => (
              <div
                key={vRow.key}
                data-index={vRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  transform: `translateY(${vRow.start}px)`,
                  display: 'grid',
                  gap: '1rem',
                  gridTemplateColumns: cols === 2 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                  paddingBottom: '1rem',
                }}
              >
                {rows[vRow.index].map(user => {
                  const req = getRequestStatus(user.id)
                  const status = req?.status
                  return (
                    <article key={user.id} className="fade-up glass-card rounded-3xl border border-white/60 p-5 shadow-lg">
                      <div className="flex items-start gap-4">
                        <button
                          type="button"
                          onClick={() => onOpenProfile?.(user.id)}
                          className="shrink-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                          aria-label={`Open ${user.name}'s profile`}
                        >
                          <img src={user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(user.name)}`} alt={user.name} className="h-16 w-16 rounded-2xl object-cover bg-orange-100" />
                        </button>
                        <div className="flex-1 min-w-0">
                          <button
                            type="button"
                            onClick={() => onOpenProfile?.(user.id)}
                            className="title-font text-left text-xl font-bold text-gray-900 truncate hover:text-orange-700"
                          >
                            {user.name}
                          </button>
                          <p className="text-sm text-gray-600 line-clamp-2">{user.bio}</p>
                          <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-orange-700">{user.location.area}, {user.location.city}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                        <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-700">{user.preferences.dietaryType}</span>
                        <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700">{user.preferences.budgetVibe}</span>
                        {user.preferences.whoWillPay && (
                          <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-700">Who will pay: {user.preferences.whoWillPay}</span>
                        )}
                        <span className={`rounded-full px-3 py-1 ${user.availability.isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {user.availability.isAvailable ? user.availability.label || 'Available' : 'Not available'}
                        </span>
                      </div>
                      <div className="mt-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Interests</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {user.preferences.interests.map(interest => (
                            <span key={`${user.id}-${interest}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${user.sharedInterests.includes(interest) ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-700'}`}>{interest}</span>
                          ))}
                        </div>
                      </div>
                      {currentUserId ? (
                        <>
                          {status === 'ACCEPTED' && (
                            <div className="mt-5 w-full rounded-2xl bg-green-100 px-4 py-3 text-sm font-semibold text-green-700 text-center">✅ Connected</div>
                          )}
                          {status === 'PENDING' && (
                            <div className="mt-5 w-full rounded-2xl bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-700 text-center">⏳ Invite Pending</div>
                          )}
                          {(status === 'DECLINED' || status === 'CANCELLED') && (
                            <button onClick={() => sendInvite(user.id)} className="mt-5 w-full rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 transition">
                              🍽️ Send Eat-Request Invite
                            </button>
                          )}
                          {!req && (
                            <button onClick={() => sendInvite(user.id)} className="mt-5 w-full rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 transition">
                              🍽️ Send Eat-Request Invite
                            </button>
                          )}
                        </>
                      ) : (
                        <p className="mt-5 text-center text-xs text-gray-400">Log in to send an invite</p>
                      )}
                    </article>
                  )
                })}
              </div>
            ))}
          </div>
        )}
        {loadingMore && (
          <div className="py-5 text-center text-sm text-gray-500">Loading more...</div>
        )}
        {!loading && !loadingMore && !hasMoreUsers && users.length > 0 && (
          <div className="py-5 text-center text-sm text-gray-400">You've seen everyone ✨</div>
        )}
      </div>
    </section>
  )
}
